import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { countriesOptions } from '@/locale';
import {
  inputRegex, optionalFieldWithRegex, errorMessages
} from '@/reusableInputConfig/reusableInputConfig';

interface Option {
  value: string;
  displayValue: string;
}

interface FormField {
  label: string;
  id: string;
  type: string;
  options?: Option[];
}

type FormFields = Record<string, FormField>;

export const formFields: FormFields = {
  billingSameAsPostal: {
    label: 'Same as postal address',
    id: 'billingSameAsPostal',
    type: 'boolean',
  },
  address1: {
    label: 'Address line 1',
    id: 'address1',
    type: 'text',
  },
  address2: {
    label: 'Address line 2',
    id: 'address2',
    type: 'text',
  },
  address3: {
    label: 'Address line 3',
    id: 'address3',
    type: 'text',
  },
  town: {
    label: 'Town',
    id: 'town',
    type: 'text',
  },
  postcode: {
    label: 'Postcode',
    id: 'postcode',
    type: 'text',
  },
  country: {
    label: 'Country',
    id: 'country',
    options: countriesOptions,
    type: 'text',
  },
};

export const getRules = () => ({
  [formFields.billingSameAsPostal.id]: yup.boolean(),
  [formFields.country.id]: yup.string().required(errorMessages.country),

  [formFields.address1.id]: yup.string()
    .when(formFields.billingSameAsPostal.id, {
      is: (val: boolean) => Boolean(val),
      then: schema => schema.optional(),
      otherwise: schema => schema
        // Having to manually recreate requiredFieldWithRegex here, due to the additional logic
        .required(errorMessages.address1)
        .max(50, 'Please enter a maximum of 50 characters')
        .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true))
        .test('regex-check', inputRegex.ADDRESS.ERROR, value => (value ? Boolean(value.match(inputRegex.ADDRESS.PATTERN)) : false))
    }),

  [formFields.address2.id]: optionalFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR),
  [formFields.address3.id]: optionalFieldWithRegex(inputRegex.ADDRESS.PATTERN, inputRegex.ADDRESS.ERROR),

  [formFields.town.id]: yup.string()
    .when(formFields.billingSameAsPostal.id, {
      is: (val: boolean) => Boolean(val),
      then: schema => schema.optional(),
      otherwise: schema => schema
        // More manual recreation of requiredFieldWithRegex:
        .required(errorMessages.town)
        .max(50, 'Please enter a maximum of 50 characters')
        .test('extra-spaces', inputRegex.EXTRA_SPACES.ERROR, value => (value ? Boolean(value.match(inputRegex.EXTRA_SPACES.PATTERN)) : true))
        .test('regex-check', inputRegex.ADDRESS.ERROR, value => (value ? Boolean(value.match(inputRegex.ADDRESS.PATTERN)) : false))
    }),

  [formFields.postcode.id]: yup.string()
    .when(formFields.billingSameAsPostal.id, {
      is: (val: boolean) => Boolean(val),
      then: schema => schema.optional(),
      otherwise: schema => schema
        .when(formFields.country.id, {
          is: 'GB',
          then: () => schema
            .required(inputRegex.UK_POSTCODE.ERROR)
            .matches(
              inputRegex.UK_POSTCODE.PATTERN,
              inputRegex.UK_POSTCODE.ERROR
            ),
          otherwise: () => schema
            .required(inputRegex.NON_UK_POSTCODE.ERROR)
            .matches(
              inputRegex.NON_UK_POSTCODE.PATTERN,
              inputRegex.NON_UK_POSTCODE.ERROR
            ),
        })
    })
});

export const getFormSchema = () => (
  yup.object().shape(getRules())
);

export const getFormResolver = () => (
  yupResolver(getFormSchema())
);
